/**
 * CoinGecko Data Transformer
 *
 * Transforms CoinGecko API responses to our internal types
 */

import { Cryptocurrency } from '../types'

/**
 * Transform CoinGecko market data to our Cryptocurrency type
 */
export function transformCoinGeckoCrypto(
  coinData: any,
  rank: number,
): Cryptocurrency {
  return {
    id: coinData.id,
    name: coinData.name,
    symbol: coinData.symbol.toUpperCase(),
    rank: rank,
    logo: coinData.image,
    trustScore: calculateTrustScore(coinData),
    price: coinData.current_price || 0,
    marketCap: coinData.market_cap || 0,
    volume24h: coinData.total_volume || 0,
    change24h: coinData.price_change_percentage_24h || 0,
    description: '',
    website: '',
    reviews: [],
    pros: [],
    cons: [],
  }
}

/**
 * Calculate trust score based on various factors
 */
function calculateTrustScore(coinData: any): number {
  let score = 5.0 // Base score

  // Market cap factor
  const marketCap = coinData.market_cap || 0
  if (marketCap > 100000000000)
    score += 2.0 // > $100B
  else if (marketCap > 10000000000)
    score += 1.5 // > $10B
  else if (marketCap > 1000000000)
    score += 1.0 // > $1B
  else if (marketCap > 100000000) score += 0.5 // > $100M

  // Market cap rank factor
  const rank = coinData.market_cap_rank || 1000
  if (rank <= 10) score += 1.5
  else if (rank <= 50) score += 1.0
  else if (rank <= 100) score += 0.5

  // Volume factor
  const volume = coinData.total_volume || 0
  if (volume > 1000000000) score += 0.5 // > $1B volume

  // ATH factor (coins that maintained value)
  const athChangePercentage = coinData.ath_change_percentage || -100
  if (athChangePercentage > -20) score += 0.5 // Within 20% of ATH

  // Cap at 10.0
  return Math.min(score, 10.0)
}

/**
 * Transform CoinGecko global data
 */
export function transformGlobalData(globalData: any) {
  const data = globalData.data
  return {
    totalMarketCap: data.total_market_cap?.usd || 0,
    total24hVolume: data.total_volume?.usd || 0,
    btcDominance: data.market_cap_percentage?.btc || 0,
    activeCryptocurrencies: data.active_cryptocurrencies || 0,
    markets: data.markets || 0,
  }
}
